
import { CFG_LVL_TO_APP_QUALITY_LVL, VIDEO_QUALITY_LEVELS } from './constants';

const { LOW, STANDARD, HIGH, AUTO } = VIDEO_QUALITY_LEVELS;
const videoQualityLevels = [ LOW, STANDARD, HIGH, AUTO ];

/**
 * Video frame heights mapped to the translation key that names them for the user.
 *
 * @type {Object}
 */
const RESOLUTION_TO_TRANSLATION_KEY = {
    '1080': 'videoStatus.hd',
    '720': 'videoStatus.hd',
    '480': 'videoStatus.sd',
    '360': 'videoStatus.sd',
    '240': 'videoStatus.ld'
};

/**
 * The keys above as numbers, ascending.
 *
 * @type {number[]}
 */
const RESOLUTIONS = Object.keys(RESOLUTION_TO_TRANSLATION_KEY)
    .map(resolution => parseInt(resolution, 10))
    .sort((a, b) => a - b);

/**
 * How far under a nominal resolution a frame may land and still count as that resolution.
 *
 * Simulcast layers are produced by dividing the capture by `scaleResolutionDownBy`, and encoders round the result
 * to their own alignment - a layer aimed at 360 routinely arrives as 358 or 356. Matched exactly, those fall a
 * single line short and drop a whole tier: a 358-line frame was reported as LD when it is the SD layer. Five
 * percent absorbs that rounding and is far narrower than the gap between two tiers, so no frame is ever promoted
 * past the tier it genuinely belongs to.
 *
 * @type {number}
 */
const RESOLUTION_MATCH_TOLERANCE = 0.95;

/**
 * Names a video frame height for the user - "HD", "SD" or "LD".
 *
 * Shared by the web quality label and the native large-video quality button, which each carried their own copy of
 * this and had already drifted. Callers must pass the *short* side of the frame: "480p" and "720p" name the short
 * side, so handing this a portrait frame's height reports the long one and overstates the quality by a tier.
 *
 * @param {number} resolution - The short side of the video frame, in pixels.
 * @returns {{ labelKey: string, tooltipKey: string }}
 */
export function mapResolutionToTranslationsKeys(resolution: number) {
    // Default to the lowest tier so an absent or unrecognised resolution names something rather than nothing.
    let highestMatchingResolution = RESOLUTIONS[0];

    for (let i = 0; i < RESOLUTIONS.length; i++) {
        const knownResolution = RESOLUTIONS[i];

        if (resolution >= knownResolution * RESOLUTION_MATCH_TOLERANCE) {
            highestMatchingResolution = knownResolution;
        } else {
            break;
        }
    }

    const labelKey = RESOLUTION_TO_TRANSLATION_KEY[highestMatchingResolution];

    return {
        labelKey,
        tooltipKey: `${labelKey}Tooltip`
    };
}

/**
 * Finds the nearest video quality level to the passed video quality.
 *
 * @param {number} videoQuality - The video quality.
 * @returns {number|undefined} - The found quality level.
 */
export function findNearestQualityLevel(videoQuality: number) {
    for (let i = 0; i < videoQualityLevels.length; i++) {
        const level = videoQualityLevels[i];

        if (level >= videoQuality) {
            return level;
        }
    }

    return undefined;
}

/**
 * Selects {@code VIDEO_QUALITY_LEVELS} for the given {@link availableHeight} and threshold to quality mapping.
 *
 * @param {number} availableHeight - The height to which a matching video quality level should be found.
 * @param {Map<number, number>} heightToLevel - The threshold to quality level mapping. The keys are sorted in the
 * ascending order.
 * @returns {number} The matching value from {@code VIDEO_QUALITY_LEVELS}.
 */
export function getReceiverVideoQualityLevel(availableHeight: number, heightToLevel: Map<number, number>): number {
    let selectedLevel = VIDEO_QUALITY_LEVELS.LOW;

    for (const [ levelThreshold, level ] of heightToLevel.entries()) {
        if (availableHeight >= levelThreshold) {
            selectedLevel = level;
        }
    }

    return selectedLevel;
}

/**
 * Converts {@code Object} passed in the config which represents height thresholds to vide quality level mapping to
 * a {@code Map}.
 *
 * @param {Object} minHeightForQualityLvl - The 'config.videoQuality.minHeightForQualityLvl' Object from
 * the configuration. See config.js for more details.
 * @returns {Map<number, number>|undefined} - A mapping of minimal thumbnail height required for given quality level or
 * {@code undefined} if the map contains invalid values.
 */
export function validateMinHeightForQualityLvl(minHeightForQualityLvl: Object): ?Map<number, number> {
    if (typeof minHeightForQualityLvl !== 'object'
        || Object.keys(minHeightForQualityLvl).map(lvl => Number(lvl))
            .find(lvl => lvl === null || isNaN(lvl) || lvl < 0)) {
        return undefined;
    }

    const levelsSorted
        = Object.keys(minHeightForQualityLvl)
            .map(k => Number(k))
            .sort((a, b) => a - b);
    const map = new Map();

    for (const level of levelsSorted) {
        const configQuality = minHeightForQualityLvl[level];
        const appQuality = CFG_LVL_TO_APP_QUALITY_LVL[configQuality];

        if (!appQuality) {
            return undefined;
        }

        map.set(level, appQuality);
    }

    return map;
}
